<script setup lang="ts">
import type { LocaleObject } from "@nuxtjs/i18n/dist/module";

defineProps<{
  variant: "slider" | "micro-combo" | "micro-star";
  containerAttrs?: Record<string, unknown>;
  widgetAttrs?: Record<string, unknown>;
}>();

const isAppInteractive = useIsAppInteractive();
const { status } = useScript(
  {
    src: "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js",
    async: true,
    type: "text/javascript",
    referrerpolicy: false,
    crossorigin: false,
  },
  {
    trigger: isAppInteractive,
  },
);

const widget = useTemplateRef("trustpilot-widget");

watch([status, widget], ([newStatus, _]) => {
  if (newStatus !== "loaded") return;

  nextTick(() => {
    if (!widget.value || !(window as any)?.Trustpilot) return;

    (window as any).Trustpilot.loadFromElement?.(widget.value, true);
  });
});

const { locale } = useI18n();
const LOCALE_REVIEW_LANG_MAP: Record<LocaleObject["code"], string> = {
  pt: "pt",
  br: "pt",
  en: "en",
  us: "en",
  de: "de",
  es: "es",
  fr: "fr",
  it: "it",
  nl: "nl",
  id: "id,en",
  ja: "ja,en",
  ko: "ko,en",
  tr: "tr,en",
  zh: "zh,en",
};

const variantAttrs = computed(() => ({
  "micro-star": {
    "data-template-id": "5419b732fbfb950b10de65e5",
    "data-style-height": "20px",
  },
  "micro-combo": {
    "data-template-id": "5419b6ffb0d04a076446a9af",
    "data-style-height": "40px",
  },
  slider: {
    "data-template-id": "54ad5defc6454f065c28af8b",
    "data-style-height": "240px",
    "data-stars": "4,5",
    "data-review-languages": LOCALE_REVIEW_LANG_MAP[locale.value],
  },
}));
</script>

<template>
  <div
    v-if="status !== 'error'"
    v-bind="containerAttrs"
  >
    <!-- @vue-expect-error vue thinks `data-locale` doesn't exist but the trustpilot script reads it -->
    <div
      :key="`${locale}-${variant}`"
      ref="trustpilot-widget"
      data-test="trustpilot-widget"
      class="trustpilot-widget"
      :class="`trustpilot-widget--${variant}`"
      :data-locale="locale"
      data-businessunit-id="642e2c2dcb3cdecbc7610c50"
      data-style-width="100%"
      data-font-family="Roboto"
      v-bind="{ ...variantAttrs[variant], ...widgetAttrs }"
    >
      <a
        href="https://uk.trustpilot.com/review/matchwornshirt.com"
        target="_blank"
        rel="noopener noreferrer"
      >
        <BaseSkeletonLoader
          v-if="variant === 'slider'"
          class="sm:mx-12"
        />
        <span class="sr-only">Trustpilot</span>
      </a>
    </div>
  </div>
</template>

<style scoped>
.trustpilot-widget {
  &--micro-combo {
    height: 20px;
    @media (max-width: 450px) {
      height: 40px;
    }
  }

  &--micro-star {
    height: 20px;
  }
}
</style>
